// src/pages/Education/EducationPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { EducationAI, getLang, buildResources } from "../../lib/educationApi";

export default function EducationPage() {
  const lang = getLang();
  const { systemPrompt } = useMemo(() => buildResources(lang), [lang]);

  // UI 텍스트
  const ui = useMemo(() => {
    if (lang === "zh-CN") {
      return {
        title: "새싹 신규직원 교육챗봇 ver.1.3",
        subtitle: "신규 직원을 위한 업무 매뉴얼 챗봇",
        faq: "자주 묻는 질문:",
        inputPh: "업무에 대해 무엇이든 물어보세요...",
        ttsTitle: "스크립트 음성으로 듣기",
        roleTitle: "상황별 응대 연습",
        welcome: "您好！我是新员工培训聊天机器人 AI 睿智萍。<br><br>请提问好奇的事项，或使用 ✨ 按钮进行情景应对练习，使用 🔊 按钮练习听脚本语音！",
        startRolePlay: "✨ <strong>情景应对练习</strong>开始。AI 将扮演患者提出棘手的情况。请根据手册尽全力回答！稍后将生成场景。",
        scenarioTitle: "<strong>患者场景:</strong><br>",
        scenarioAsk: "<br><br>您将如何应对？请输入回答。",
        ttsEmpty: "请输入要转换为语音的脚本。",
        error: "抱歉。发生了错误。请稍后再试。",
      };
    }
    return {
      title: "새싹 신규직원 교육챗봇 ver.1.3",
      subtitle: "신규 직원을 위한 업무 매뉴얼 챗봇",
      faq: "자주 묻는 질문:",
      inputPh: "업무에 대해 무엇이든 물어보세요...",
      ttsTitle: "스크립트 음성으로 듣기",
      roleTitle: "상황별 응대 연습",
      welcome: "안녕하세요! 새싹 신규직원 교육챗봇 AI 예지핑 입니다.<br><br>궁금한 점을 질문하시거나, ✨ 버튼으로 상황별 응대 연습, 🔊 버튼으로 스크립트 음성 듣기 연습을 해보세요!",
      startRolePlay: "✨ <strong>상황별 응대 연습</strong>을 시작합니다. AI가 환자 역할을 맡아 까다로운 상황을 제시할 거예요. 매뉴얼에 따라 최선을 다해 답변해주세요! 잠시 후 시나리오가 생성됩니다.",
      scenarioTitle: "<strong>환자 시나리오:</strong><br>",
      scenarioAsk: "<br><br>어떻게 응대하시겠어요? 답변을 입력해주세요.",
      ttsEmpty: "음성으로 변환할 스크립트를 입력해주세요.",
      error: "죄송합니다. 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
    };
  }, [lang]);

  // 채팅 상태
  const [messages, setMessages] = useState(/** @type {Array<{sender:'user'|'ai', html:string}>} */ ([]));
  const [query, setQuery] = useState("");
  const [isRolePlaying, setIsRolePlaying] = useState(false);
  const [scenario, setScenario] = useState("");
  const chatRef = useRef(null);

  // 스크롤 고정
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // 초기 웰컴 메시지
  useEffect(() => {
    const timer = setTimeout(() => {
      appendAI(ui.welcome);
    }, 400);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  function appendAI(html) {
    setMessages((prev) => [...prev, { sender: "ai", html }]);
  }
  function appendUser(text) {
    setMessages((prev) => [...prev, { sender: "user", html: escapeHtml(text) }]);
  }

  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]));
  }

  // ✅ onSend: FAQ 클릭 시 인풋 거치지 않고 바로 전송 가능
  async function onSend(nextText) {
    const q = (typeof nextText === "string" ? nextText : query).trim();
    if (!q) return;

    appendUser(q);

    // 인풋에서 보낸 경우만 비우기 (FAQ 클릭은 인풋 상태 유지)
    if (typeof nextText !== "string") setQuery("");

    try {
      if (isRolePlaying) {
        // 롤플레이 답변 피드백
        const { feedback } = await EducationAI.feedback({
          scenario,
          answerFromStaff: q,
        });
        appendAI(feedback?.replace(/\n/g, "<br>") || ui.error);
        setIsRolePlaying(false);
        setScenario("");
        return;
      }

      // 일반 질의
      const { answer } = await EducationAI.ask({ query: q });
      appendAI(answer?.replace(/\n/g, "<br>") || ui.error);
    } catch (e) {
      appendAI(ui.error);
    }
  }

  async function onStartRolePlay() {
    // 안내 메시지
    appendAI(ui.startRolePlay);
    try {
      const { scenario: sc } = await EducationAI.scenario();
      const scHtml = `${ui.scenarioTitle}${(sc || "").replace(/\n/g, "<br>")}${ui.scenarioAsk}`;
      appendAI(scHtml);
      setIsRolePlaying(true);
      setScenario(sc || "");
    } catch (e) {
      appendAI(ui.error);
    }
  }

  function onSpeak() {
    const text = query.trim();
    if (!text) {
      appendAI(ui.ttsEmpty);
      return;
    }
    try {
      if ("speechSynthesis" in window) {
        const utter = new SpeechSynthesisUtterance(text);
        utter.lang = lang === "zh-CN" ? "zh-CN" : "ko-KR";
        utter.rate = 1.0;
        utter.pitch = 1.0;
        window.speechSynthesis.speak(utter);
      } else {
        appendAI(ui.error);
      }
    } catch {
      appendAI(ui.error);
    }
  }

  // 추천 질문들
    const suggestions = lang === "zh-CN"
        ? [
            // 1. 중국어 약 이름 변수 + 중국어 문장 결합
            `${process.env.REACT_APP_CN_SIKTAM_SAYAK}的服用方法是什么`,

            // 2. 나머지 문장들도 모두 중국어로 변경
            "请说明预约规则",
            "韩药药效可维持多久？",
            "请告诉我前台的主要工作"
        ]
        : [
            // 한국어 버전 (백틱 사용으로 깔끔하게 정리)
            `${process.env.REACT_APP_SIKTAM_SAYAK} 복용법 알려줘`,
            "예약 규칙 알려줘",
            "한약 약효 유지기간은?",
            "데스크 주요 업무 알려줘"
        ];

  return (
    <section className="edu">
      {/* 공통 헤더 블록 */}
      <div id="content_title">
        <h2>{ui.title}</h2>
        <p>{ui.subtitle}</p>
      </div>

      <div className="content">
        <div className="chatbox" style={{ minHeight: "60vh" }}>
          <div ref={chatRef} className="chatlog">
            {messages.map((m, i) => (
              <div key={i} className={`bubble ${m.sender === "user" ? "from-user" : "from-patient"}`}>
                <div className="text" dangerouslySetInnerHTML={{ __html: m.html }} />
              </div>
            ))}
          </div>
        </div>

        {/* 추천 질문 */}
        <div className="">
          <p className="inputTitle">{ui.faq}</p>
          <div className="suglst">
            {suggestions.map((q) => (
              <button
                key={q}
                className=""
                onClick={() => {
                  // ✅ 인풋 거치지 않고 바로 채팅으로 전송
                  onSend(q);
                }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* 입력 영역 */}
        <div className="edu_question">
          <div className="">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={ui.inputPh}
              className=""
              onKeyDown={(e) => {
                if (e.key === "Enter") onSend();
              }}
            />
            <button title={ui.ttsTitle} className="speak" onClick={onSpeak}>
              🔊
            </button>
            <button title={ui.roleTitle} className="play" onClick={onStartRolePlay}>
              ✨
            </button>
            <button className="send" onClick={() => onSend()}>
              ➤
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
